import { JSONSerializable } from '../util/json';
import { Denom } from './Denom';
import { Dec, Int, Numeric } from './numeric';

export class Coin extends JSONSerializable<Coin.Data> implements Numeric<Coin> {
  public readonly amount: Numeric.Output;

  constructor(public readonly denom: Denom, amount: Numeric.Input) {
    super();
    this.amount = Numeric.parse(amount);
  }

  public static fromData(data: Coin.Data): Coin {
    const { denom, amount } = data;
    return new Coin(denom, amount);
  }

  public isIntCoin(): boolean {
    // TODO: convert into typeguard
    return this.amount instanceof Int;
  }

  public isDecCoin(): boolean {
    return this.amount instanceof Dec;
  }

  public toIntCoin(): Coin {
    return new Coin(this.denom, new Int(this.amount));
  }

  public toDecCoin(): Coin {
    return new Coin(this.denom, new Dec(this.amount));
  }

  public toData(): Coin.Data {
    const { denom, amount } = this;
    return {
      denom,
      amount: amount.toString(),
    };
  }

  public toString(): string {
    return `${this.amount.toFixed()}${this.denom}`;
  }

  public add(other: Numeric.Input | Coin): Coin {
    let otherAmount;
    if (other instanceof Coin) {
      if (other.denom !== this.denom) {
        throw new Coin.ArithmeticError(
          `cannot add two Coins of different denoms: ${this.denom} and ${other.denom}`
        );
      }
      otherAmount = other.amount;
    } else {
      otherAmount = other;
    }

    otherAmount = Numeric.parse(otherAmount);
    return new Coin(this.denom, this.amount.add(otherAmount));
  }

  public sub(other: Numeric.Input | Coin): Coin {
    let otherAmount;
    if (other instanceof Coin) {
      if (other.denom !== this.denom) {
        throw new Coin.ArithmeticError(
          `cannot subtract two Coins of different denoms: ${this.denom} and ${other.denom}`
        );
      }
      otherAmount = other.amount;
    } else {
      otherAmount = other;
    }

    otherAmount = Numeric.parse(otherAmount);
    return new Coin(this.denom, this.amount.sub(otherAmount));
  }

  public mul(other: Numeric.Input): Coin {
    const otherAmount = Numeric.parse(other);
    return new Coin(this.denom, this.amount.mul(otherAmount));
  }

  public div(other: Numeric.Input): Coin {
    const otherAmount = Numeric.parse(other);
    return new Coin(this.denom, this.amount.div(otherAmount));
  }

  public mod(other: Numeric.Input): Coin {
    const otherAmount = Numeric.parse(other);
    return new Coin(this.denom, this.amount.mod(otherAmount));
  }
}

export namespace Coin {
  export interface Data {
    denom: Denom;
    amount: string;
  }

  export class ArithmeticError {
    constructor(public readonly message: string) {}
  }
}
